import type { CasOrCategory, ReleaseProfileID } from '../../types/db.ts';
import type {
  ReleaseProfileFormula,
  ReleaseProfileFormulaItem,
  ReleaseProfileVariableForFrontendRead,
  ReleaseProfilesAndVariables,
} from '../../types/release-profiles.ts';
import type { ReleaseProfileBrief } from '../../types/tri.ts';
import type {
  ComplianceMetricSnapshot,
  ComplianceMetricSnapshotFormula,
  ComplianceMetricSnapshotIfChoice,
  ComplianceMetricSnapshotVariable,
} from '../../types/emissions.ts';
import { complianceMetricSnapshotVersion } from '../../types/emissions.ts';
import { VariableNameFinder } from '../formulas/variable-name-finder.ts';
import { formulaPredefinedVariables } from '../formulas/formula-utils.ts';

/**
 * A compliance metric is a release profile plus its `emissionsComplianceMetricConfig`, and a limit built on one is
 * evaluated against whatever that profile happens to say today. Editing the profile therefore silently rewrites what
 * every saved limit measures.
 *
 * This module records what a limit was saved against — a projection holding only the parts of the metric that can move
 * the number or invalidate the limit — so that drift can be detected and, more usefully, described. Compare with
 * fingerprinting the same projection: a hash answers "did this change", and the snapshot it is derived from answers
 * "what changed", which is what a user has to know before accepting it.
 *
 * Everything cosmetic — profile, formula, variable and answer names — is kept in a parallel `labels` block that the
 * diff skips, so renaming a variable never breaks a limit but a rename is still available to describe one that did.
 */

const complianceOutputTypes = new Set(['complianceNumerator', 'complianceDenominator']);

export type ComplianceMetricChangeSeverity =
  /** The limit cannot be trusted as configured: it must be reviewed and re-saved before it evaluates again. */
  | 'breaking'
  /** The limit still measures what it always did, but the value it produces will move. */
  | 'value'
  /** Recorded for completeness; affects neither this limit's result nor its validity. */
  | 'cosmetic';

export interface ComplianceMetricDefinitionChange {
  /** Where in the snapshot the change is, e.g. ['variables', 'fuel_burned', 'value']. */
  path: string[];
  kind: 'added' | 'removed' | 'changed';
  before?: unknown;
  after?: unknown;
  severity: ComplianceMetricChangeSeverity;
  message: string;
}

// ---------------------------------------------------------------------------
// Building a snapshot
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const collator = (left: string, right: string) => (left < right ? -1 : left > right ? 1 : 0);

/**
 * Every formula the metric's compliance outputs are computed from: the ones carrying such an output, plus everything
 * they reach through variable references. A formula outside that closure cannot affect the limit, so editing it is not
 * a change to this limit's definition.
 */
function complianceFormulaClosure(formulaObj: ReleaseProfileFormula): Map<string, ReleaseProfileFormulaItem> {
  const formulasByVariableName = new Map(
    Object.values(formulaObj.formulas).map((formula) => [formula.nameForVariable, formula]),
  );
  const included = new Map<string, ReleaseProfileFormulaItem>();

  const visit = (formula: ReleaseProfileFormulaItem) => {
    if (included.has(formula.formulaID)) {
      return;
    }
    included.set(formula.formulaID, formula);
    const found = VariableNameFinder.findInString(formula.formula);
    // A formula we cannot parse is recorded verbatim, and its own text is what a user would have to fix anyway; we
    // simply cannot see past it to its dependencies.
    if (found.status === 'error') {
      return;
    }
    for (const name of found.variableNames) {
      const referenced = formulasByVariableName.get(name);
      if (referenced) {
        visit(referenced);
      }
    }
  };

  for (const formula of Object.values(formulaObj.formulas)) {
    if (Object.values(formula.outputs).some((output) => complianceOutputTypes.has(output.type))) {
      visit(formula);
    }
  }

  return included;
}

function snapshotFormula(formula: ReleaseProfileFormulaItem): ComplianceMetricSnapshotFormula {
  const outputs: ComplianceMetricSnapshotFormula['outputs'] = {};
  for (const [outputID, output] of Object.entries(formula.outputs)) {
    if (!complianceOutputTypes.has(output.type)) {
      continue;
    }
    outputs[outputID] = {
      type: output.type,
      chemicals: Array.isArray(output.chemicals) ? [...output.chemicals].sort(collator) : output.chemicals,
      ifChoices: output.ifChoices
        .map((condition) => ({ choiceListID: condition.choiceListID, choiceID: condition.choiceID }))
        .sort((left, right) =>
          collator(`${left.choiceListID}\u0000${left.choiceID}`, `${right.choiceListID}\u0000${right.choiceID}`),
        ),
    };
  }
  return { nameForVariable: formula.nameForVariable, formula: formula.formula, outputs };
}

function snapshotVariable(
  name: string,
  variablesByName: Map<string, ReleaseProfileVariableForFrontendRead>,
  derivedNames: Set<string>,
  predefinedUnitsByName: Map<string, string>,
): ComplianceMetricSnapshotVariable {
  // A formula of the metric's own wins over a release profile variable of the same name, matching how the evaluator
  // substitutes sub-formulas before it resolves anything else.
  if (derivedNames.has(name)) {
    return { kind: 'derived' };
  }

  const variable = variablesByName.get(name);
  if (variable) {
    const definition = variable.definition;
    if (definition.type === 'constant') {
      return { kind: 'constant', units: variable.units, value: definition.value };
    }
    if (definition.type === 'question') {
      return {
        kind: 'question',
        units: variable.units,
        questionID: definition.questionID,
        isChemicalSpecific: definition.isChemicalSpecific,
      };
    }
    if (definition.type === 'chemicalProfile') {
      const chemicals: Partial<Record<CasOrCategory, number>> = {};
      for (const chemical of definition.chemicals) {
        chemicals[chemical.casOrCategory] = chemical.value;
      }
      return {
        kind: 'chemicalProfile',
        units: variable.units,
        chemicals,
        usageTypesApplied: [...definition.usageTypesApplied].sort((left, right) => left - right),
      };
    }
    // Mix calculations, usage mixes and waste streams are rejected outright for compliance metrics, but a metric that
    // somehow reaches one is better described than silently ignored.
    return { kind: 'unsupported', units: variable.units, definitionType: definition.type };
  }

  const predefinedUnits = predefinedUnitsByName.get(name);
  if (predefinedUnits !== undefined) {
    return { kind: 'predefined', units: predefinedUnits };
  }

  return { kind: 'missing' };
}

/**
 * Projects a compliance metric down to what a limit built on it depends on. Returns null when the release profile is
 * not a compliance metric at all, which is itself a change worth reporting — see complianceMetricDefinitionChanges.
 */
export function complianceMetricSnapshotBuild(
  profile: Pick<
    ReleaseProfileBrief,
    'releaseProfileID' | 'name' | 'formula' | 'isArchived' | 'emissionsComplianceMetricConfig'
  >,
  profiles: Pick<
    ReleaseProfilesAndVariables,
    'constants' | 'questions' | 'chemicalProfiles' | 'mixCalculations' | 'usageMixes' | 'wasteStreams'
  >,
): ComplianceMetricSnapshot | null {
  const config = profile.emissionsComplianceMetricConfig;
  if (!config) {
    return null;
  }

  const variablesByName = new Map<string, ReleaseProfileVariableForFrontendRead>(
    [
      ...profiles.constants,
      ...profiles.questions,
      ...profiles.chemicalProfiles,
      ...profiles.mixCalculations,
      ...profiles.usageMixes,
      ...profiles.wasteStreams,
    ].map((variable) => [variable.nameForVariable, variable]),
  );
  const predefinedUnitsByName = new Map(
    formulaPredefinedVariables.map((variable) => [variable.nameForVariable, variable.units]),
  );

  const closure = complianceFormulaClosure(profile.formula);
  const derivedNames = new Set(Array.from(closure.values(), (formula) => formula.nameForVariable));

  const formulas: ComplianceMetricSnapshot['formulas'] = {};
  const formulaLabels: Record<string, string> = {};
  const referencedVariableNames = new Set<string>();
  for (const [formulaID, formula] of closure) {
    formulas[formulaID] = snapshotFormula(formula);
    formulaLabels[formulaID] = formula.nameFriendly;
    const found = VariableNameFinder.findInString(formula.formula);
    if (found.status === 'success') {
      for (const name of found.variableNames) {
        referencedVariableNames.add(name);
      }
    }
  }

  const variables: ComplianceMetricSnapshot['variables'] = {};
  const variableLabels: Record<string, string> = {};
  for (const name of Array.from(referencedVariableNames).sort(collator)) {
    variables[name] = snapshotVariable(name, variablesByName, derivedNames, predefinedUnitsByName);
    const label = variablesByName.get(name)?.nameFriendly ?? closureFormulaLabel(closure, name);
    if (label !== undefined) {
      variableLabels[name] = label;
    }
  }

  // Only the choice lists a compliance output is conditioned on: a condition whose list or answer has been deleted can
  // never match again, which silently stops the metric from producing anything.
  const choiceLists: ComplianceMetricSnapshot['choiceLists'] = {};
  const choiceListLabels: Record<string, string> = {};
  const choiceLabels: Record<string, string> = {};
  for (const formula of Object.values(formulas)) {
    for (const output of Object.values(formula.outputs)) {
      for (const condition of output.ifChoices) {
        if (choiceLists[condition.choiceListID]) {
          continue;
        }
        const choiceList = profile.formula.choiceLists[condition.choiceListID];
        choiceLists[condition.choiceListID] = {
          choiceIDs: (choiceList?.choices ?? []).map((choice) => choice.choiceID).sort(collator),
        };
        if (choiceList) {
          choiceListLabels[condition.choiceListID] = choiceList.question;
          for (const choice of choiceList.choices) {
            choiceLabels[choice.choiceID] = choice.title;
          }
        }
      }
    }
  }

  return {
    version: complianceMetricSnapshotVersion,
    releaseProfileID: profile.releaseProfileID,
    isArchived: profile.isArchived,
    config: {
      valueSemantics: config.valueSemantics,
      expectedUnits: config.expectedUnits,
      unitGrouping: config.unitGrouping,
      chemicalGrouping: config.chemicalGrouping,
      productGrouping: config.productGrouping,
      windowReduction: config.windowReduction,
      window: config.window,
    },
    formulas,
    variables,
    choiceLists,
    labels: {
      metricName: profile.name,
      formulas: formulaLabels,
      variables: variableLabels,
      choiceLists: choiceListLabels,
      choices: choiceLabels,
    },
  };
}

function closureFormulaLabel(closure: Map<string, ReleaseProfileFormulaItem>, name: string): string | undefined {
  for (const formula of closure.values()) {
    if (formula.nameForVariable === name) {
      return formula.nameFriendly;
    }
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Diffing two snapshots
// ---------------------------------------------------------------------------

interface RawChange {
  path: string[];
  kind: 'added' | 'removed' | 'changed';
  before?: unknown;
  after?: unknown;
}

/**
 * Key order is not preserved by Postgres jsonb, so a stored snapshot read back rarely matches a freshly built one
 * byte for byte. Structural walking is already order-independent; this canonicalizes the leaves it compares whole.
 */
function canonical(value: unknown): string {
  if (value === undefined) {
    return 'undefined';
  }
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([left], [right]) => collator(left, right));
  return `{${entries.map(([key, entryValue]) => `${JSON.stringify(key)}:${canonical(entryValue)}`).join(',')}}`;
}

function pathMatches(path: readonly string[], pattern: readonly string[]): boolean {
  return (
    path.length === pattern.length && pattern.every((segment, index) => segment === '*' || segment === path[index])
  );
}

/** A default's value is reported whole — "the window changed to rolling 12 months", not "months was added". */
const leafPatterns: readonly (readonly string[])[] = [['config', '*', 'value']];

function isLeafPath(path: readonly string[], saved: unknown, current: unknown): boolean {
  if (leafPatterns.some((pattern) => pathMatches(path, pattern))) {
    return true;
  }
  // A variable that changed kind entirely is one change, not a scattering of field-level ones:
  return (
    path.length === 2 && path[0] === 'variables' && isRecord(saved) && isRecord(current) && saved.kind !== current.kind
  );
}

function walk(saved: unknown, current: unknown, path: string[], out: RawChange[]): void {
  if (isRecord(saved) && isRecord(current) && !isLeafPath(path, saved, current)) {
    for (const key of Array.from(new Set([...Object.keys(saved), ...Object.keys(current)])).sort(collator)) {
      if (!(key in current)) {
        out.push({ path: [...path, key], kind: 'removed', before: saved[key] });
      } else if (!(key in saved)) {
        out.push({ path: [...path, key], kind: 'added', after: current[key] });
      } else {
        walk(saved[key], current[key], [...path, key], out);
      }
    }
    return;
  }

  if (canonical(saved) !== canonical(current)) {
    out.push({ path, kind: 'changed', before: saved, after: current });
  }
}

// ---------------------------------------------------------------------------
// Describing a change
// ---------------------------------------------------------------------------

interface SnapshotLabels {
  metric: string;
  formula(formulaID: string): string;
  variable(name: string): string;
  choiceList(choiceListID: string): string;
  choice(choiceID: string): string;
}

function labelsFor(saved: ComplianceMetricSnapshot, current: ComplianceMetricSnapshot): SnapshotLabels {
  const pick = (group: keyof Omit<ComplianceMetricSnapshot['labels'], 'metricName'>, key: string) =>
    current.labels[group][key] ?? saved.labels[group][key] ?? key;
  return {
    metric: current.labels.metricName || saved.labels.metricName,
    formula: (formulaID) => pick('formulas', formulaID),
    variable: (name) => pick('variables', name),
    choiceList: (choiceListID) => pick('choiceLists', choiceListID),
    choice: (choiceID) => pick('choices', choiceID),
  };
}

const configSettingLabels: Record<string, string> = {
  expectedUnits: 'comparison unit',
  unitGrouping: 'source grouping',
  chemicalGrouping: 'chemical grouping',
  productGrouping: 'product grouping',
  windowReduction: 'window reduction',
  window: 'evaluation window',
};

const valueSemanticsLabels: Record<string, string> = {
  'period-total': 'tracking-period total',
  'period-average': 'tracking-period average',
};

const outputTypeLabels: Record<string, string> = {
  complianceNumerator: "the ratio's numerator",
  complianceDenominator: "the ratio's denominator",
};

const variableKindLabels: Record<string, string> = {
  constant: 'constant',
  question: 'question',
  chemicalProfile: 'chemical profile',
  predefined: 'built-in input',
  derived: 'formula of this metric',
  unsupported: 'unsupported variable',
  missing: 'missing variable',
};

function valueToText(value: unknown): string {
  if (value === null || value === undefined) {
    return 'nothing';
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? 'blank' : `"${value}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? 'nothing' : value.map(valueToText).join(', ');
  }
  if (isRecord(value) && typeof value.type === 'string') {
    return typeof value.months === 'number' ? `${value.type} (${value.months} months)` : value.type;
  }
  return canonical(value);
}

function fromTo(change: RawChange): string {
  return `from ${valueToText(change.before)} to ${valueToText(change.after)}`;
}

function conditionsToText(value: unknown, labels: SnapshotLabels): string[] {
  return Array.isArray(value)
    ? (value as ComplianceMetricSnapshotIfChoice[]).map(
        (condition) => `${labels.choiceList(condition.choiceListID)} = ${labels.choice(condition.choiceID)}`,
      )
    : [];
}

/** Describes what an ordered set gained and lost, rather than printing both versions of it in full. */
function setDifferenceToText(before: string[], after: string[]): string {
  const added = after.filter((entry) => !before.includes(entry));
  const removed = before.filter((entry) => !after.includes(entry));
  const parts: string[] = [];
  if (added.length > 0) {
    parts.push(`added ${added.join(', ')}`);
  }
  if (removed.length > 0) {
    parts.push(`removed ${removed.join(', ')}`);
  }
  return parts.length > 0 ? parts.join(' and ') : 'reordered';
}

type SeverityResolver =
  | ComplianceMetricChangeSeverity
  | ((change: RawChange, current: ComplianceMetricSnapshot) => ComplianceMetricChangeSeverity);

interface ChangeRule {
  pattern: readonly string[];
  severity: SeverityResolver;
  describe: (change: RawChange, labels: SnapshotLabels) => string;
}

function configSettingIsMandatory(change: RawChange, current: ComplianceMetricSnapshot): boolean {
  const setting = current.config[change.path[1] as keyof ComplianceMetricSnapshot['config']];
  return isRecord(setting) && setting.mandatory === true;
}

/**
 * Matched in order, first match wins, so more specific patterns come first. Anything unmatched falls through to a
 * generic breaking change — a field added to the snapshot and never classified here becomes loudly conservative
 * rather than silently ignored.
 */
const changeRules: readonly ChangeRule[] = [
  {
    pattern: ['isArchived'],
    severity: (change) => (change.after === true ? 'breaking' : 'cosmetic'),
    describe: (change, labels) =>
      change.after === true
        ? `The compliance metric "${labels.metric}" has been archived.`
        : `The compliance metric "${labels.metric}" has been restored from the archive.`,
  },
  {
    pattern: ['config', 'valueSemantics'],
    severity: 'breaking',
    describe: (change) =>
      `The metric now reports a ${valueSemanticsLabels[String(change.after)] ?? valueToText(change.after)} rather than a ${
        valueSemanticsLabels[String(change.before)] ?? valueToText(change.before)
      }.`,
  },
  {
    pattern: ['config', '*', 'mandatory'],
    severity: (change) => (change.after === true ? 'breaking' : 'cosmetic'),
    describe: (change) => {
      const setting = configSettingLabels[change.path[1]!] ?? change.path[1]!;
      return change.after === true
        ? `The metric now requires its own ${setting}; this limit may no longer be allowed to differ.`
        : `The metric no longer requires its own ${setting}.`;
    },
  },
  {
    pattern: ['config', '*', 'value'],
    severity: (change, current) => (configSettingIsMandatory(change, current) ? 'breaking' : 'cosmetic'),
    describe: (change) =>
      `The metric's default ${configSettingLabels[change.path[1]!] ?? change.path[1]!} changed ${fromTo(change)}.`,
  },
  {
    pattern: ['config', '*'],
    severity: (change, current) =>
      change.kind === 'changed' && configSettingIsMandatory(change, current) ? 'breaking' : 'cosmetic',
    describe: (change) => {
      const setting = configSettingLabels[change.path[1]!] ?? change.path[1]!;
      const after = isRecord(change.after) ? change.after.value : change.after;
      return after === null || after === undefined
        ? `The metric no longer recommends a ${setting}.`
        : `The metric now recommends a ${setting} of ${valueToText(after)}.`;
    },
  },
  {
    pattern: ['formulas', '*'],
    severity: 'breaking',
    describe: (change, labels) =>
      change.kind === 'added'
        ? `The formula "${labels.formula(change.path[1]!)}" was added to this metric.`
        : `The formula "${labels.formula(change.path[1]!)}" is no longer part of this metric.`,
  },
  {
    pattern: ['formulas', '*', 'formula'],
    severity: 'breaking',
    describe: (change, labels) => `The expression for "${labels.formula(change.path[1]!)}" changed ${fromTo(change)}.`,
  },
  {
    pattern: ['formulas', '*', 'nameForVariable'],
    severity: 'breaking',
    describe: (change, labels) =>
      `"${labels.formula(change.path[1]!)}" is referenced by other formulas as ${valueToText(change.after)} rather than ${valueToText(change.before)}.`,
  },
  {
    pattern: ['formulas', '*', 'outputs', '*'],
    severity: 'breaking',
    describe: (change, labels) => {
      const type = isRecord(change.after) ? change.after.type : isRecord(change.before) ? change.before.type : null;
      const which = outputTypeLabels[String(type)] ?? 'a compliance output';
      return change.kind === 'added'
        ? `"${labels.formula(change.path[1]!)}" now also produces ${which}.`
        : `"${labels.formula(change.path[1]!)}" no longer produces ${which}.`;
    },
  },
  {
    pattern: ['formulas', '*', 'outputs', '*', 'type'],
    severity: 'breaking',
    describe: (change, labels) =>
      `The output of "${labels.formula(change.path[1]!)}" moved from ${
        outputTypeLabels[String(change.before)] ?? valueToText(change.before)
      } to ${outputTypeLabels[String(change.after)] ?? valueToText(change.after)}.`,
  },
  {
    pattern: ['formulas', '*', 'outputs', '*', 'chemicals'],
    severity: 'breaking',
    describe: (change, labels) =>
      `The chemicals measured by "${labels.formula(change.path[1]!)}" changed ${fromTo(change)}.`,
  },
  {
    pattern: ['formulas', '*', 'outputs', '*', 'ifChoices'],
    severity: 'breaking',
    describe: (change, labels) => {
      const before = conditionsToText(change.before, labels);
      const after = conditionsToText(change.after, labels);
      if (before.length === 0) {
        return `"${labels.formula(change.path[1]!)}" now only applies when ${after.join(' and ')}.`;
      }
      if (after.length === 0) {
        return `"${labels.formula(change.path[1]!)}" now always applies, rather than only when ${before.join(' and ')}.`;
      }
      return `The conditions under which "${labels.formula(change.path[1]!)}" applies changed: ${setDifferenceToText(before, after)}.`;
    },
  },
  {
    pattern: ['variables', '*'],
    severity: 'breaking',
    describe: (change, labels) => {
      const name = labels.variable(change.path[1]!);
      if (change.kind === 'added') {
        return `The metric now reads "${name}".`;
      }
      if (change.kind === 'removed') {
        return `The metric no longer reads "${name}".`;
      }
      const before = isRecord(change.before) ? String(change.before.kind) : '';
      const after = isRecord(change.after) ? String(change.after.kind) : '';
      return `"${name}" changed from a ${variableKindLabels[before] ?? before} to a ${variableKindLabels[after] ?? after}.`;
    },
  },
  {
    pattern: ['variables', '*', 'value'],
    severity: 'value',
    describe: (change, labels) => `The constant "${labels.variable(change.path[1]!)}" changed ${fromTo(change)}.`,
  },
  {
    pattern: ['variables', '*', 'units'],
    severity: 'breaking',
    describe: (change, labels) => `The units of "${labels.variable(change.path[1]!)}" changed ${fromTo(change)}.`,
  },
  {
    pattern: ['variables', '*', 'questionID'],
    severity: 'breaking',
    describe: (change, labels) => `"${labels.variable(change.path[1]!)}" now asks a different question.`,
  },
  {
    pattern: ['variables', '*', 'isChemicalSpecific'],
    severity: 'breaking',
    describe: (change, labels) =>
      change.after === true
        ? `"${labels.variable(change.path[1]!)}" is now answered separately for each chemical.`
        : `"${labels.variable(change.path[1]!)}" is no longer answered separately for each chemical.`,
  },
  {
    pattern: ['variables', '*', 'definitionType'],
    severity: 'breaking',
    describe: (change, labels) => `"${labels.variable(change.path[1]!)}" changed ${fromTo(change)}.`,
  },
  {
    pattern: ['variables', '*', 'chemicals', '*'],
    severity: 'value',
    describe: (change, labels) => {
      const name = labels.variable(change.path[1]!);
      const casOrCategory = change.path[3]!;
      if (change.kind === 'added') {
        return `The chemical profile "${name}" now covers ${casOrCategory}, at ${valueToText(change.after)}.`;
      }
      if (change.kind === 'removed') {
        return `The chemical profile "${name}" no longer covers ${casOrCategory}.`;
      }
      return `The chemical profile "${name}" changed its ${casOrCategory} value ${fromTo(change)}.`;
    },
  },
  {
    pattern: ['variables', '*', 'usageTypesApplied'],
    severity: 'value',
    describe: (change, labels) =>
      `The usage types the chemical profile "${labels.variable(change.path[1]!)}" applies to changed.`,
  },
  {
    pattern: ['choiceLists', '*'],
    severity: 'breaking',
    describe: (change, labels) =>
      change.kind === 'added'
        ? `A compliance output is now conditioned on "${labels.choiceList(change.path[1]!)}".`
        : `A compliance output is no longer conditioned on "${labels.choiceList(change.path[1]!)}".`,
  },
  {
    pattern: ['choiceLists', '*', 'choiceIDs'],
    severity: 'value',
    describe: (change, labels) => {
      const before = (Array.isArray(change.before) ? change.before : []).map((id) => labels.choice(String(id)));
      const after = (Array.isArray(change.after) ? change.after : []).map((id) => labels.choice(String(id)));
      if (after.length === 0) {
        return `The question "${labels.choiceList(change.path[1]!)}" no longer exists, so any output conditioned on it can never apply.`;
      }
      return `The answers available for "${labels.choiceList(change.path[1]!)}" changed: ${setDifferenceToText(before, after)}.`;
    },
  },
];

function ruleFor(path: readonly string[]): ChangeRule | null {
  return changeRules.find((rule) => pathMatches(path, rule.pattern)) ?? null;
}

function describeUnclassified(change: RawChange): string {
  const where = change.path.join('.');
  if (change.kind === 'added') {
    return `The compliance metric gained ${where} (${valueToText(change.after)}).`;
  }
  if (change.kind === 'removed') {
    return `The compliance metric lost ${where} (${valueToText(change.before)}).`;
  }
  return `The compliance metric's ${where} changed ${fromTo(change)}.`;
}

/** Compares two snapshots and explains every difference between them. */
export function complianceMetricSnapshotDiff(
  saved: ComplianceMetricSnapshot,
  current: ComplianceMetricSnapshot,
): ComplianceMetricDefinitionChange[] {
  // `labels` exists to describe changes, never to be one; `version` is the snapshot's own format, not the metric's.
  const { labels: _savedLabels, version: _savedVersion, ...savedComparable } = saved;
  const { labels: _currentLabels, version: _currentVersion, ...currentComparable } = current;

  const raw: RawChange[] = [];
  walk(savedComparable, currentComparable, [], raw);

  const labels = labelsFor(saved, current);
  return raw.map((change) => {
    const rule = ruleFor(change.path);
    const severity = !rule
      ? 'breaking'
      : typeof rule.severity === 'function'
        ? rule.severity(change, current)
        : rule.severity;
    return {
      path: change.path,
      kind: change.kind,
      before: change.before,
      after: change.after,
      severity,
      message: rule ? rule.describe(change, labels) : describeUnclassified(change),
    };
  });
}

// ---------------------------------------------------------------------------
// Applying it to a saved limit
// ---------------------------------------------------------------------------

const severityRank: Record<ComplianceMetricChangeSeverity, number> = { cosmetic: 0, value: 1, breaking: 2 };

export function complianceMetricChangesWorstSeverity(
  changes: ComplianceMetricDefinitionChange[],
): ComplianceMetricChangeSeverity | null {
  return changes.reduce<ComplianceMetricChangeSeverity | null>(
    (worst, change) => (!worst || severityRank[change.severity] > severityRank[worst] ? change.severity : worst),
    null,
  );
}

export function complianceMetricBreakingChanges(
  changes: ComplianceMetricDefinitionChange[],
): ComplianceMetricDefinitionChange[] {
  return changes.filter((change) => change.severity === 'breaking');
}

/**
 * Everything about the compliance metric behind a limit that has changed since the limit was saved.
 *
 * Returns nothing when there is no evidence of a change — a limit that measures emissions rather than a metric, or one
 * saved before snapshots were recorded — because a limit is only wrong when we can show that it is.
 */
export function complianceMetricDefinitionChanges(
  limit: { releaseProfileID: ReleaseProfileID | null; complianceMetricSnapshot: ComplianceMetricSnapshot | null },
  profiles: Pick<
    ReleaseProfilesAndVariables,
    | 'releaseProfiles'
    | 'constants'
    | 'questions'
    | 'chemicalProfiles'
    | 'mixCalculations'
    | 'usageMixes'
    | 'wasteStreams'
  >,
): ComplianceMetricDefinitionChange[] {
  const saved = limit.complianceMetricSnapshot;
  if (limit.releaseProfileID === null || !saved) {
    return [];
  }

  // A snapshot written in a format this build no longer understands is not a change to the metric, and reporting it as
  // one would break every saved limit on deploy. The next save rewrites it in the current format.
  if (saved.version !== complianceMetricSnapshotVersion) {
    return [];
  }

  const profile = profiles.releaseProfiles.find((candidate) => candidate.releaseProfileID === limit.releaseProfileID);
  if (!profile) {
    return [
      {
        path: ['releaseProfile'],
        kind: 'removed',
        before: limit.releaseProfileID,
        severity: 'breaking',
        message: `The compliance metric "${saved.labels.metricName}" no longer exists.`,
      },
    ];
  }

  const current = complianceMetricSnapshotBuild(profile, profiles);
  if (!current) {
    return [
      {
        path: ['config'],
        kind: 'removed',
        before: saved.config,
        severity: 'breaking',
        message: `The release profile "${profile.name}" is no longer a compliance metric.`,
      },
    ];
  }

  return complianceMetricSnapshotDiff(saved, current);
}

/** One sentence naming what changed, for a verdict or a badge that has no room to list every change. */
export function complianceMetricChangesSummary(
  changes: ComplianceMetricDefinitionChange[],
  metricName: string,
): string {
  const listed = changes.slice(0, 2).map((change) => change.message);
  const remaining = changes.length - listed.length;
  const tail = remaining > 0 ? ` (${remaining} more change${remaining === 1 ? '' : 's'}.)` : '';
  return `The compliance metric "${metricName}" changed after this limit was saved. ${listed.join(' ')}${tail} Review and save the limit to accept.`;
}
